import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Progress,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Separator,
    Slider,
    Textarea,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components';

import { BIAS_FILTERS } from '@/config/app';
import { FileUpload, Icons } from '@/components/common';

interface UserInputProps {
    handleFileContent: (content: string) => void;
    handleTextChange: (text: string) => void;
    analyzeText: () => void;
    handleReset: () => void;
    handleTest: () => void;
    isAnalyzing: boolean;
    progress: number;
    sensitivity: number;
    setSensitivity: (value: number) => void;
    biasFilter: string;
    setBiasFilter: (value: string) => void;
    searchTerm: string;
    maxChars: number;
    setShowGuidedTour: (show: boolean) => void;
}

// Text input area component
const TextInputArea = ({
    searchTerm,
    handleTextChange,
    maxChars,
}: {
    searchTerm: string;
    handleTextChange: (text: string) => void;
    maxChars: number;
}) => (
    <div className="relative">
        <Textarea
            placeholder="Enter your text here..."
            value={searchTerm}
            onChange={(e) => handleTextChange(e.target.value)}
            className="mb-1"
            rows={5}
            aria-label="analysis-textarea"
            data-testid="analysis-textarea"
        />
        <div className="text-sm text-gray-500 text-right">
            {searchTerm.length}/{maxChars} characters
        </div>
    </div>
);

// Primary action buttons component
const PrimaryActions = ({
    handleFileContent,
    analyzeText,
    searchTerm,
    isAnalyzing,
}: {
    handleFileContent: (content: string) => void;
    analyzeText: () => void;
    searchTerm: string;
    isAnalyzing: boolean;
}) => (
    <div className="flex flex-col sm:flex-row items-center gap-2">
        <FileUpload onFileContent={handleFileContent} />
        <Button
            className="flex-1 text-lg py-6 w-full sm:w-auto mt-2 sm:mt-0"
            size="lg"
            onClick={analyzeText}
            disabled={searchTerm.length === 0 || isAnalyzing}
        >
            <Icons.send className="h-4 w-4 mr-2" />
            {isAnalyzing ? 'Detecting...' : 'Detect Bias'}
        </Button>
    </div>
);

// Helper actions component
const HelperActions = ({
    handleTest,
    setShowGuidedTour,
    handleReset,
}: {
    handleTest: () => void;
    setShowGuidedTour: (show: boolean) => void;
    handleReset: () => void;
}) => (
    <div className="flex flex-col">
        <label className="text-sm font-medium" htmlFor="bias-filter-select">
            Helper Actions
        </label>
        <div className="flex flex-wrap items-center gap-2 mt-1">
            <Button
                variant="outline"
                size="lg"
                onClick={handleTest}
                className="text-sm"
            >
                <Icons.test className="h-4 w-4 mr-2" />
                Load Sample
            </Button>
            <Button
                variant="outline"
                size="lg"
                onClick={() => setShowGuidedTour(true)}
                className="text-sm"
            >
                <Icons.bookOpen className="h-4 w-4 mr-2" />
                Guided Tour
            </Button>
            <Button
                className="border-red-700 text-red-700 hover:border-red-800 hover:text-red-800 text-sm"
                variant="outline"
                size="lg"
                onClick={handleReset}
            >
                <Icons.refresh className="h-4 w-4 mr-2" />
                Reset
            </Button>
        </div>
    </div>
);

// Tooltip label component
const TooltipLabel = ({
    label,
    tooltip,
    testId,
}: {
    label: string;
    tooltip: string;
    testId?: string;
}) => (
    <label className="text-sm font-medium">
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger
                    className="flex items-center cursor-pointer"
                    data-testid={testId || 'tooltip-label'}
                >
                    {label}
                    <Icons.info className="h-4 w-4 ml-1 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                    <p>{tooltip}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    </label>
);

// Bias filter component
const BiasFilterControl = ({
    biasFilter,
    setBiasFilter,
}: {
    biasFilter: string;
    setBiasFilter: (value: string) => void;
}) => (
    <div className="w-full sm:w-[250px]">
        <TooltipLabel
            label="Bias Filter"
            tooltip="Filter the bias categories to display in the results."
            testId="bias-filter-tooltip"
        />
        <Select value={biasFilter} onValueChange={setBiasFilter}>
            <SelectTrigger id="bias-filter-select" className="mt-1">
                <SelectValue placeholder="Select filter" />
            </SelectTrigger>
            <SelectContent>
                {BIAS_FILTERS.map((filter, i) => (
                    <SelectItem key={i} value={filter.value}>
                        {filter.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

// Sensitivity slider component
const SensitivityControl = ({
    sensitivity,
    setSensitivity,
}: {
    sensitivity: number;
    setSensitivity: (value: number) => void;
}) => (
    <div className="w-full sm:w-[200px]">
        <TooltipLabel
            label="Bias Sensitivity"
            tooltip="Adjust the sensitivity of the bias detection algorithm. Lower the sensitivity to detect more biases and vice versa."
        />
        <Slider
            className="my-2 py-2"
            value={[sensitivity]}
            onValueChange={(value) => setSensitivity(value[0])}
            max={100}
            step={1}
            aria-label="bias-sensitivity-slider"
            data-testid="bias-sensitivity-slider"
        />
    </div>
);

// Controls section component
const ControlsSection = ({
    handleTest,
    setShowGuidedTour,
    handleReset,
    biasFilter,
    setBiasFilter,
    sensitivity,
    setSensitivity,
}: {
    handleTest: () => void;
    setShowGuidedTour: (show: boolean) => void;
    handleReset: () => void;
    biasFilter: string;
    setBiasFilter: (value: string) => void;
    sensitivity: number;
    setSensitivity: (value: number) => void;
}) => (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <HelperActions
            handleTest={handleTest}
            setShowGuidedTour={setShowGuidedTour}
            handleReset={handleReset}
        />

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4 md:mt-0">
            <BiasFilterControl
                biasFilter={biasFilter}
                setBiasFilter={setBiasFilter}
            />
            <SensitivityControl
                sensitivity={sensitivity}
                setSensitivity={setSensitivity}
            />
        </div>
    </div>
);

export const UserInput = ({
    handleFileContent,
    handleTextChange,
    analyzeText,
    handleReset,
    handleTest,
    isAnalyzing,
    progress,
    sensitivity,
    setSensitivity,
    biasFilter,
    setBiasFilter,
    searchTerm,
    maxChars,
    setShowGuidedTour,
}: UserInputProps) => {
    return (
        <Card className="mb-4">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="font-bold md:text-3xl text-2xl">
                        Bias Lens – Explainable Bias Detection
                    </CardTitle>
                    <CardDescription>
                        Upload a file or type your text to assess potential
                        bias.
                    </CardDescription>
                </div>
            </CardHeader>

            <CardContent>
                {/* Text Input */}
                <TextInputArea
                    searchTerm={searchTerm}
                    handleTextChange={handleTextChange}
                    maxChars={maxChars}
                />

                {/* Button Row */}
                <div className="mt-4 flex flex-col gap-4">
                    <PrimaryActions
                        handleFileContent={handleFileContent}
                        analyzeText={analyzeText}
                        searchTerm={searchTerm}
                        isAnalyzing={isAnalyzing}
                    />

                    <Separator />

                    <ControlsSection
                        handleTest={handleTest}
                        setShowGuidedTour={setShowGuidedTour}
                        handleReset={handleReset}
                        biasFilter={biasFilter}
                        setBiasFilter={setBiasFilter}
                        sensitivity={sensitivity}
                        setSensitivity={setSensitivity}
                    />
                </div>

                {/* Progress Bar */}
                {isAnalyzing && (
                    <div className="pb-2">
                        <Progress value={progress} className="mt-4" />
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
